import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BicycleSpecKey } from "../../../../commons/specs/bicycle";
import type { FilterValue, ProductFilterValues } from "../../../../commons/specs/filters";
import { gearSeriesGroups, getGearBrandKeyForSeries, type GearBrandKey } from "../../../../commons/specs/gear";
import { ProductType } from "../../../../commons/specs/product";

type GearSeriesFilterValue = FilterValue<ProductType.Bicycle, BicycleSpecKey.GearSeriesKey>;
type GearSeriesFilterItem = { value: GearSeriesFilterValue; label: string };

export interface GearSeriesGroup {
  brandKey: GearBrandKey;
  brandLabel: string;
  items: GearSeriesFilterItem[];
}

/** Same shape as `GearSeriesGroup`, but without the localized `brandLabel`. */
export type RawGearSeriesGroup = Omit<GearSeriesGroup, "brandLabel">;

/**
 * Groups `filterValues` by gear brand using the shared gear catalog, preserving
 * catalog order across brands and within each brand. Unknown series end up in
 * the `UNKNOWN` group. Empty groups are dropped.
 *
 * The returned objects intentionally omit `brandLabel`; the hook below
 * resolves the localized label and produces full `GearSeriesGroup`s.
 */
export const groupGearSeriesFilterValues = (
  filterValues: NonNullable<ProductFilterValues<ProductType.Bicycle>[BicycleSpecKey.GearSeriesKey]>
): RawGearSeriesGroup[] => {
  const bucketed = new Map<GearBrandKey, GearSeriesFilterItem[]>();

  for (const filterValue of filterValues as GearSeriesFilterItem[]) {
    const brandKey = getGearBrandKeyForSeries(filterValue.value);
    const list = bucketed.get(brandKey) ?? [];
    list.push(filterValue);
    bucketed.set(brandKey, list);
  }

  return gearSeriesGroups.flatMap(({ brandKey, seriesKeys }): RawGearSeriesGroup[] => {
    const items = bucketed.get(brandKey);
    if (!items) {
      return [];
    }

    const orderIndex = new Map<string, number>(seriesKeys.map((key, index) => [key, index]));
    // Unknown values are sorted deterministically by value after catalog-known values.
    const sortedItems = [...items].sort((a, b) => {
      const orderDiff =
        (orderIndex.get(a.value) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.value) ?? Number.MAX_SAFE_INTEGER);

      if (orderDiff !== 0) {
        return orderDiff;
      } else {
        return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
      }
    });

    return [{ brandKey, items: sortedItems }];
  });
};

/**
 * Group the incoming gearSeries filter values by gear brand using the shared
 * catalog, preserving catalog order both across brands and within each brand.
 * Empty groups are dropped. Unknown series end up in the `UNKNOWN` group.
 */
export const useGearSeriesGroups = (
  filterValues: NonNullable<ProductFilterValues<ProductType.Bicycle>[BicycleSpecKey.GearSeriesKey]>
): GearSeriesGroup[] => {
  const { t } = useTranslation(["commons"]);

  return useMemo(
    () =>
      groupGearSeriesFilterValues(filterValues).map(group => ({
        ...group,
        brandLabel: t(
          `commons:productFilterConfig.bicycle.productFilter.gearSeriesKey.groupLabels.${group.brandKey}` as `commons:productFilterConfig.bicycle.productFilter.gearSeriesKey.groupLabels.${GearBrandKey}`
        )
      })),
    [filterValues, t]
  );
};
